import { addError } from '@shared/utils/logger';
import { useEventInfo } from '@shared/utils/eventInfo';
import { useEventCallback } from '@shared/utils/hooks/useEventCallback';
import { UpdateTravelMapComponentMutation, useUpdateTravelMapComponentMutation, useUpdateAccommodationsSortOrderDataQuery } from '@graphql/generated';
import { sortByOrderList } from '@shared/utils/array';

/**
 * To update the sort order of accommodations places in a travel map component.
 *
 * @param {function} [onCompleted] - Optional callback invoked when the mutation completes successfully.
 * @param {function} [onError] - Optional callback invoked when the mutation failed.
 * @returns {{
 *   loadingSortOrderData: boolean,
 *   updatingAccommodationsSortOrder: boolean,
 *   updateAccommodationsSortOrder: update mutation function
 * }} Object containing the update function and a loading state.
 */
export function useUpdateAccommodationsSortOrder(onCompleted?: (data: UpdateTravelMapComponentMutation) => void, onError?: (error: Error) => void) {
  const handleOnError = useEventCallback((error: Error) => {
    addError(error, { context: 'Accommodations sort order update failed.' });
    onError?.(error);
  });

  const { eventHandle, loading: loadingEventInfo } = useEventInfo();
  const { data, loading: loadingSortOrders } = useUpdateAccommodationsSortOrderDataQuery({
    batchMode: 'fast',
    variables: {
      payload: {
        eventName: eventHandle!,
        pageSlug: 'accommodations',
      },
      options: {
        admin: true,
      },
      name: eventHandle!,
    },
    skip: !eventHandle || loadingEventInfo,
  });
  const findTravelMap = data?.pageContainerBySlug?.components?.find(component => component.__typename === 'TravelMapComponent');
  const travelMapComponent = findTravelMap?.__typename === 'TravelMapComponent' ? findTravelMap : undefined;
  const existingAccommodationsPlaceIds = data?.eventByName?.accommodationPlacesV2.map(accommodationsPlace => accommodationsPlace.id);

  const [updateTravelMapComponent, { loading: updatingSortOrder }] = useUpdateTravelMapComponentMutation({ onCompleted, onError: handleOnError });

  /**
   * Triggers a mutation to update the order of accommodation blocks.
   *
   * @param {Object} params - Parameters to determine how to update the ordering.
   * @param {string[]} params.accommodationsPlaceIds - IDs of the accommodations to add or remove.
   * @param {'add' | 'remove'} params.action - Action to perform: add or remove the accommodation.
   */
  const updateSortOrder = useEventCallback(({ accommodationsPlaceIds, action }: { accommodationsPlaceIds: string[]; action: 'add' | 'remove' }) => {
    if (!travelMapComponent) return;

    // Get or calculate initial sort order for accommodations places
    const initialBlockOrdering = () => {
      const { blockOrdering } = travelMapComponent;

      // If there's no blockOrdering, fallback to existing place IDs
      if (!blockOrdering?.length) {
        return existingAccommodationsPlaceIds ?? [];
      }

      // If blockOrdering exists but doesn't match the number of places, reorder
      const needsReordering = existingAccommodationsPlaceIds && existingAccommodationsPlaceIds.length !== blockOrdering.length;
      if (needsReordering) {
        const idObjects = existingAccommodationsPlaceIds.map(id => ({ id }));
        return sortByOrderList(idObjects, blockOrdering).map(obj => obj.id);
      }

      // Otherwise, use the existing blockOrdering as-is
      return blockOrdering;
    };

    const blockOrdering = action === 'remove' ? initialBlockOrdering().filter(id => !accommodationsPlaceIds.includes(id)) : [...initialBlockOrdering(), ...accommodationsPlaceIds];

    updateTravelMapComponent({
      variables: {
        payload: {
          id: travelMapComponent.id,
          sortOrder: travelMapComponent.sortOrder,
          blockOrdering,
        },
      },
      refetchQueries: ['PageContainerBySlug'],
    });
  });

  return { loadingSortOrders: loadingSortOrders || loadingEventInfo, updatingSortOrder, updateSortOrder };
}
